import { ReactNode, useMemo } from 'react';
import { AnyActivity } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { FilterBar } from '@Components/FilterBar';
import { Checkbox } from '@Components/FormCheck';
import { CheckState, Size } from '@Components/designConstants';
import { useActivityFeedData, useActivityFeedFilterContext } from '../useActivityFeedData';
import { useMultiSelect } from '../useActivityFeedMultiSelect';
import { useShowWipActions } from '../activity-feed-utils';
import { BulkActionsMenu } from './BulkActionsMenu';
import { CreateEventModal } from './CreateEventModal';
import { ACTIVITY_SEARCH_COLUMNS } from './activity-feed-list-utils';

export const FeedListHeader = (): ReactNode => {
  const { activities } = useActivityFeedData();
  const { setSearchPredicate } = useActivityFeedFilterContext();
  const showWip = useShowWipActions();

  return (
    <div className='activity-feed-list-header'>
      <div className='activity-feed-list-header-search'>
        <FilterBar<AnyActivity>
          options={ACTIVITY_SEARCH_COLUMNS}
          lines={activities}
          // The updater form is required because searchPredicate is itself a function — passing
          // it bare would make React treat it as a `(prev) => next` reducer and invoke it.
          onFilter={predicate => setSearchPredicate(() => predicate)}
          placeholder='Search'
          size={Size.MD}
          useLocalStorage={false}
        />
        {showWip && <CreateEventModal />}
      </div>
      <SelectionRow />
    </div>
  );
};

const SelectionRow = (): ReactNode => {
  const { activityIds, activitiesById } = useActivityFeedData();
  const { checkedIds, checkState, totalCount, setAllChecked } = useMultiSelect();

  const checkedActivities = useMemo(
    () => activityIds.filter(id => checkedIds.has(id)).map(id => activitiesById[id]),
    [activityIds, checkedIds, activitiesById]
  );

  return (
    <div className='activity-feed-list-header-selection'>
      <Checkbox
        aria-label='Select all events'
        checked={checkState === CheckState.CHECKED}
        indeterminate={checkState === CheckState.INDETERMINATE}
        disabled={totalCount === 0}
        onChange={() => setAllChecked(checkState === CheckState.UNCHECKED)}
      />
      <span>{checkedIds.size.toLocaleString()} Selected</span>
      <BulkActionsMenu checkedActivities={checkedActivities} />
      <span className='activity-feed-list-header-total'>{totalCount.toLocaleString()} Total</span>
    </div>
  );
};
